/** @jsx h */
import { h, JSX } from 'preact';
import style from './index.css';
import sharedStyles from '../common/shared-style.css';
import { progressBarStyle, brandBarStyle } from '../common/shared-style';
import {
  sendAnalyticsTrack,
  sendCreativeCloseButtonClick,
  sendCreativeExpand
} from '../../../../../common/js/TrackAnalyticsHelper';
import { hexToRGBA } from '../../../../../common/js/colors';
import VastIcon from '../../vast-icon';
import cn from 'classnames';
import { RootStore } from '../../../../../common/js/stores/root.store';
import { ResponsiveControlsStrategy } from '../../../../../common/js/stores/controls/responsive-controls-strategy';
import { VastIconType } from '../../../lib/vast/vast-icon';
import OptOutIcon from '../../../../../common/components/opt-out/icon';
import CustomizedControl from '../../../../../common/components/customized-control';
import ProgressBar from '../../../../../common/components/progress-bar';
import BrandItem from '../../../../../common/components/brand-item';
import SoundControl from '../../../../../common/components/sound-control';
import ExpandControl from '../../../../../common/components/expand-control';
import { CloseThumbnailControl } from '../../../../../common/components/close-thumbnail-control';

interface ResponsiveProps {
  elapsedTime: number;
  onForceClose: (e: MouseEvent) => void;
  onOptout: () => void;
  onToggleMute: (e: Event) => void;
  videoDuration: number;
  isVideoMuted: boolean;
  closingScheduledIn?: number;
  onToggleExpand: (isExpanded: boolean) => void;
  showClose?: boolean;
  _debug?: boolean;
  vastIcon?: VastIconType;
  onClickVastIcon?: (url: string) => void;
  clickCloseTime?: number;
  isVertical?: boolean;
}

export default function Responsive({
  elapsedTime,
  onForceClose,
  onOptout,
  onToggleMute,
  videoDuration,
  isVideoMuted,
  closingScheduledIn,
  onToggleExpand,
  showClose = true,
  _debug = false,
  vastIcon,
  clickCloseTime,
  isVertical = false
}: ResponsiveProps): JSX.Element {
  const rootStore = RootStore.getInstance();
  const controlsStore: ResponsiveControlsStrategy = rootStore.controlsStore as ResponsiveControlsStrategy;
  const videoStore = rootStore.videoStore;
  const configStore = rootStore.configStore;

  const { controls } = configStore.config;

  const isExpanded = videoStore.isVideoExpanded.value;
  const isVideoCompleted = videoStore.isVideoCompleted.value;
  const closePosition =
    configStore.config.closeBtn && configStore.config.closeBtn.position;

  const optoutIconSize = 12;
  const closeIconSize = 10;
  const soundIconSize = 12;
  const expandIconSize = 14;

  const shouldDisplayBrandItem = controlsStore.shouldDisplayBrandItem();
  const shouldDisplayMuteButton = controlsStore.shouldDisplayMuteButton();

  const shouldDisplaySoundItemInBottom =
    shouldDisplayBrandItem && shouldDisplayMuteButton;
  const shouldDisplaySoundItemInTop =
    !shouldDisplayBrandItem && shouldDisplayMuteButton;

  const controlStyle = {
    backgroundColor: controls?.backgroundColor,
    backgroundOpacity:
      controls?.backgroundOpacity && controls.backgroundOpacity / 100,
    labelColor: controls?.labelColor,
    labelOpacity: controls?.labelOpacity && controls.labelOpacity / 100
  };

  const backgroundStyle: h.JSX.CSSProperties = {
    background: hexToRGBA(
      controls?.backgroundColor ? controls.backgroundColor : '#28323c',
      controls?.backgroundOpacity ? controls.backgroundOpacity / 100 : 0.5
    ) as string,
    width: '20px',
    height: '20px',
    borderRadius: '4px'
  };

  let optOutPositionStyle: Record<string, string | number> = {};
  if (!controlsStore.shouldHideOptout()) {
    optOutPositionStyle = {
      right: 0,
      left: 'auto'
    };
  }

  // Brand bar
  const brandBarPosition = {
    left: closePosition === 'left' ? 'auto' : 4,
    right: closePosition === 'right' ? 'auto' : 4,
    padding: 4
  };

  const brandBarContainerStyle = {
    background: hexToRGBA(
      controls?.backgroundColor ? controls.backgroundColor : '#28323c',
      controls?.backgroundOpacity ? controls.backgroundOpacity / 100 : 0.5
    ) as string
  };

  const isVastItemShifted =
    (closePosition === 'left' &&
      !isVideoCompleted &&
      shouldDisplayMuteButton) ||
    (closePosition === 'right' && showClose);

  const vastItem = vastIcon ? (
    <VastIcon
      style={{ right: isVastItemShifted ? '28px' : 0 }}
      imageStyle={{ width: '50px' }}
      icon={vastIcon}
    />
  ) : null;

  const optOutItem = (
    <div className={sharedStyles.optout} style={optOutPositionStyle}>
      <OptOutIcon
        iconSize={optoutIconSize}
        onClick={onOptout}
        isInterstitialAdUnit={false}
        style={backgroundStyle}
      />
    </div>
  );

  const closeItem = (
    <div className={sharedStyles.close} data-zone="close">
      <CustomizedControl
        id="close"
        onTouchStart={(e: TouchEvent) => e.stopPropagation()}
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          sendAnalyticsTrack(e);
          onForceClose(e);
        }}
        controlStyle={controlStyle}
        isInterstitialAdUnit={false}
        style={backgroundStyle}
      >
        <CloseThumbnailControl
          iconSize={closeIconSize}
          showClose={showClose}
          iconColor="white"
          _debug={_debug}
          closingScheduledIn={closingScheduledIn}
          onClick={() => {
            sendCreativeCloseButtonClick();
          }}
          clickCloseTime={clickCloseTime}
          elapsedTime={elapsedTime}
        />
      </CustomizedControl>
    </div>
  );

  const soundItem = (
    <div className={sharedStyles.sound} data-zone="sound">
      <CustomizedControl
        id="sound"
        onTouchStart={(e: TouchEvent) => {
          if (!isVideoCompleted) {
            e.stopPropagation();
          }
        }}
        onClick={(e: MouseEvent) => {
          if (!isVideoCompleted) {
            e.stopPropagation();
            onToggleMute(e);
            sendAnalyticsTrack(e);
          }
        }}
        controlStyle={controlStyle}
        isInterstitialAdUnit={false}
        style={backgroundStyle}
      >
        <SoundControl iconSize={soundIconSize} isMuted={isVideoMuted} />
      </CustomizedControl>
    </div>
  );

  const expandItem = (
    <div
      className={cn({
        [sharedStyles.expand]: true,
        [style.extraExpandStyle]: shouldDisplaySoundItemInBottom
      })}
      data-zone="expand"
    >
      <CustomizedControl
        id="expand"
        onTouchStart={(e: TouchEvent) => e.stopPropagation()}
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          sendAnalyticsTrack(e);
          sendCreativeExpand(isExpanded);
          onToggleExpand(isExpanded);
        }}
        controlStyle={controlStyle}
        isInterstitialAdUnit={false}
        style={backgroundStyle}
      >
        <ExpandControl iconSize={expandIconSize} />
      </CustomizedControl>
    </div>
  );

  const progressItem = (
    <ProgressBar
      id="progress"
      adjustedFormulae={true}
      elapsedTime={elapsedTime}
      duration={videoDuration}
      style={progressBarStyle()}
      onClick={(e: MouseEvent) => {
        sendAnalyticsTrack(e);
      }}
    />
  );

  const brandBarItem = (
    <div style={brandBarPosition} data-zone="brand-bar">
      <BrandItem
        style={brandBarStyle()}
        containerStyle={brandBarContainerStyle}
        position={closePosition === 'right' ? 'left' : 'right'}
        isVertical={isVertical}
      />
    </div>
  );

  const emptyItem = shouldDisplayBrandItem ? '' : <div />;

  return (
    <div data-zone="responsive-control">
      {!controlsStore.shouldHideBorderLayer() && (
        <div className={style.borderLayer} />
      )}
      {controlsStore.shouldDisplayVastIcon() ? vastItem : ''}

      <div
        className={cn(sharedStyles.controlsRow, sharedStyles.topControls)}
        style={{
          flexDirection: closePosition === 'left' ? 'row-reverse' : 'row'
        }}
      >
        {shouldDisplaySoundItemInTop ? soundItem : emptyItem}
        {shouldDisplayBrandItem ? brandBarItem : ''}
        {showClose ? closeItem : emptyItem}
      </div>

      <div
        className={cn(sharedStyles.controlsRow, sharedStyles.bottomControls)}
      >
        {shouldDisplaySoundItemInBottom ? soundItem : ''}
        {controlsStore.shouldDisplayOptOut() ? optOutItem : <div />}
        {controlsStore.canExpand() ? expandItem : ''}
      </div>

      {controlsStore.shouldDisplayProgressBar() ? progressItem : <div />}
    </div>
  );
}
